// "Make time": pick a length, pick one of the next few free times (today or tomorrow, inside your
// working hours, in your zone), then confirm. Choosing a time only arms; a second, separate press
// confirms, and the server binds the approval to exactly that title, time and calendar. Keys
// inside the dialog: 1–4 lengths, j/k or ↑/↓ to move, Enter to choose and again to confirm, Esc
// to go back or close.
import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { CalendarClock, ShieldCheck, X } from "lucide-react";
import {
  DEFAULT_FOCUS_DURATION,
  FOCUS_DURATIONS,
  type FocusDuration,
  type FocusSlot,
} from "@shared/agenda";
import { CONFIRM_GUARD_MS } from "@web/feed/actions";
import { isApiError } from "@web/lib/api";
import { cn } from "@web/lib/cn";
import { dayApi } from "@web/lib/dayApi";
import { useNow } from "@web/lib/useNow";
import { Button } from "@web/ui/Button";
import { Kbd } from "@web/ui/Kbd";
import { Modal } from "@web/ui/Modal";
import { slotWords } from "./dayWords";
import { addFocusBlock } from "./focusOps";
import { makeTimeStore, useMakeTimeState, type MakeTimeRequest } from "./makeTimeStore";

const LENGTH_LABEL: Record<FocusDuration, string> = {
  15: "15 min",
  30: "30 min",
  60: "1 hour",
  90: "1½ hours",
};

/** Mounted once in the shell; opens over any screen. */
export function MakeTimeHost() {
  const state = useMakeTimeState();
  return (
    <Modal open={state.open} onClose={makeTimeStore.close} label="Make time">
      {state.open && state.request ? (
        <MakeTimePicker key={state.seq} request={state.request} onClose={makeTimeStore.close} />
      ) : null}
    </Modal>
  );
}

interface Armed {
  slot: FocusSlot;
  at: number;
}

function errorWords(error: unknown): string {
  if (isApiError(error, "stale") && (error.body as { reason?: string })?.reason === "title_changed")
    return "The title changed since you opened this. Look again, then choose a time.";
  if (isApiError(error, "stale")) return "That time just filled up. Pick another one.";
  if (isApiError(error, "invalid_request"))
    return error.serverMessage ?? "That time can’t be used. Pick another one.";
  return "Couldn’t reach the server. Nothing was added.";
}

function MakeTimePicker({ request, onClose }: { request: MakeTimeRequest; onClose: () => void }) {
  const queryClient = useQueryClient();
  const now = useNow(30_000);
  const [duration, setDuration] = useState<FocusDuration>(DEFAULT_FOCUS_DURATION);
  const [index, setIndex] = useState(0);
  const [armed, setArmed] = useState<Armed | null>(null);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const confirmRef = useRef<HTMLButtonElement>(null);

  const slots = useQuery({
    queryKey: ["focusSlots", request.target.kind, request.target.id, duration],
    queryFn: ({ signal }) =>
      dayApi.focusSlots({ target: request.target, durationMin: duration }, signal),
    staleTime: 30_000,
    retry: false,
  });
  const data = slots.data;
  const list = data?.slots ?? [];
  const title = data?.title ?? request.title;
  const zone = data?.timeZone ?? "UTC";

  useEffect(() => {
    rootRef.current?.focus({ preventScroll: true });
  }, []);
  useEffect(() => {
    if (armed) confirmRef.current?.focus({ preventScroll: true });
    else rootRef.current?.focus({ preventScroll: true });
  }, [armed]);

  const pickLength = (d: FocusDuration) => {
    setDuration(d);
    setIndex(0);
    setArmed(null);
    setProblem(null);
  };

  const arm = useCallback((slot: FocusSlot) => {
    setProblem(null);
    setArmed({ slot, at: Date.now() });
  }, []);

  const confirm = async () => {
    if (!armed || busy || !data?.connected) return;
    if (Date.now() - armed.at < CONFIRM_GUARD_MS) return;
    setBusy(true);
    try {
      await addFocusBlock(
        queryClient,
        {
          target: request.target,
          slot: { start: armed.slot.start, end: armed.slot.end },
          title,
          ...(request.lineUp ? { lineUp: true } : {}),
        },
        slotWords(armed.slot, Date.now(), zone).label,
      );
      onClose();
    } catch (error) {
      setProblem(errorWords(error));
      setArmed(null);
      void slots.refetch();
    } finally {
      setBusy(false);
    }
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.metaKey || e.ctrlKey || e.altKey) return;
    if (e.key === "Escape" && armed) {
      e.preventDefault();
      e.stopPropagation();
      setArmed(null);
      return;
    }
    if (armed) return; // Enter on the focused Confirm button confirms; nothing else is bound.
    const length = FOCUS_DURATIONS[Number(e.key) - 1];
    if (length) {
      e.preventDefault();
      pickLength(length);
      return;
    }
    if (!list.length) return;
    if (e.key === "j" || e.key === "ArrowDown") {
      e.preventDefault();
      setIndex((i) => Math.min(list.length - 1, i + 1));
    } else if (e.key === "k" || e.key === "ArrowUp") {
      e.preventDefault();
      setIndex((i) => Math.max(0, i - 1));
    } else if (e.key === "Enter" && !e.repeat) {
      e.preventDefault();
      const slot = list[Math.min(index, list.length - 1)];
      if (slot) arm(slot);
    }
  };

  return (
    <div
      ref={rootRef}
      tabIndex={-1}
      onKeyDown={onKeyDown}
      className="flex max-h-[85dvh] flex-col outline-none"
      data-testid="make-time"
    >
      <div className="flex items-start justify-between gap-3 border-b border-line px-5 py-4">
        <div className="min-w-0">
          <p className="mm-kicker">Make time</p>
          <h2 className="mt-1 text-xl leading-snug break-words">{title}</h2>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="rounded-md p-1 text-muted hover:bg-sunk hover:text-ink"
        >
          <X className="size-4" />
        </button>
      </div>
      <div className="flex flex-col gap-4 overflow-y-auto px-5 py-4">
        {data && !data.connected ? (
          <p className="text-sm text-ink-2">
            Your calendar isn’t connected yet.{" "}
            <Link
              to="/settings/calendar"
              onClick={onClose}
              className="text-accent-text underline underline-offset-2"
            >
              Connect Google Calendar
            </Link>{" "}
            and MeeMail can find you some time.
          </p>
        ) : armed ? (
          <ArmedPanel
            title={title}
            slot={armed.slot}
            zone={zone}
            email={data?.email ?? null}
            now={now}
            busy={busy}
            confirmRef={confirmRef}
            onBack={() => setArmed(null)}
            onConfirm={() => void confirm()}
          />
        ) : (
          <>
            <fieldset className="flex flex-col gap-2">
              <legend className="mb-2 text-sm font-medium text-ink">How long?</legend>
              <div className="flex flex-wrap gap-2">
                {FOCUS_DURATIONS.map((d, i) => (
                  <Button
                    key={d}
                    size="sm"
                    variant={d === duration ? "primary" : "secondary"}
                    aria-pressed={d === duration}
                    onClick={() => pickLength(d)}
                  >
                    {LENGTH_LABEL[d]}
                    <Kbd
                      className={cn(
                        "hidden sm:inline-flex",
                        d === duration && "border-current bg-transparent text-current opacity-80",
                      )}
                    >
                      {String(i + 1)}
                    </Kbd>
                  </Button>
                ))}
              </div>
            </fieldset>
            <section aria-label="Free times" className="flex flex-col gap-2">
              <p className="text-sm font-medium text-ink">When?</p>
              {slots.isPending ? (
                <p className="text-sm text-muted" aria-busy>
                  Looking at your calendar…
                </p>
              ) : slots.isError ? (
                <div className="flex flex-wrap items-center gap-2 text-sm text-muted">
                  Couldn’t read your calendar just now.
                  <Button size="sm" variant="ghost" onClick={() => void slots.refetch()}>
                    Try again
                  </Button>
                </div>
              ) : list.length ? (
                <ul className="flex flex-col gap-1.5" role="listbox" aria-label="Free times">
                  {list.map((slot, i) => {
                    const words = slotWords(slot, now, zone);
                    const on = i === index;
                    return (
                      <li key={slot.start} role="option" aria-selected={on}>
                        <button
                          type="button"
                          onClick={() => arm(slot)}
                          onMouseEnter={() => setIndex(i)}
                          className={cn(
                            "flex w-full items-center gap-3 rounded-lg border px-3 py-2.5 text-left",
                            on
                              ? "border-accent/50 bg-accent-soft"
                              : "border-line bg-bg hover:bg-sunk/60",
                          )}
                        >
                          <CalendarClock aria-hidden className="size-4 shrink-0 text-muted" />
                          <span className="flex min-w-0 flex-1 flex-wrap items-baseline justify-between gap-x-3">
                            <span className="whitespace-nowrap text-ink tabular-nums">
                              {words.label}
                            </span>
                            <span className="whitespace-nowrap text-sm text-muted">
                              {words.relative}
                            </span>
                          </span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              ) : null}
              {data?.notes.map((n) => (
                <p key={n} className="text-sm text-muted">
                  {n}
                </p>
              ))}
              {problem ? (
                <p role="status" className="text-sm text-note-ink">
                  {problem}
                </p>
              ) : null}
            </section>
          </>
        )}
      </div>
      {!armed && data?.connected !== false ? (
        <p className="border-t border-line px-5 py-3 text-xs text-muted">
          <span className="hidden sm:inline">
            <Kbd>1</Kbd>–<Kbd>4</Kbd> length · <Kbd>J</Kbd> <Kbd>K</Kbd> move · <Kbd>Enter</Kbd>{" "}
            choose · <Kbd>Esc</Kbd> close.{" "}
          </span>
          Choosing a time doesn’t add anything yet.
        </p>
      ) : null}
    </div>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="grid grid-cols-[5.5rem_minmax(0,1fr)] gap-2 text-sm">
      <dt className="text-muted">{label}</dt>
      <dd className="min-w-0 break-words text-ink">{children}</dd>
    </div>
  );
}

function ArmedPanel({
  title,
  slot,
  zone,
  email,
  now,
  busy,
  confirmRef,
  onBack,
  onConfirm,
}: {
  title: string;
  slot: FocusSlot;
  zone: string;
  email: string | null;
  now: number;
  busy: boolean;
  confirmRef: React.RefObject<HTMLButtonElement | null>;
  onBack: () => void;
  onConfirm: () => void;
}) {
  const words = slotWords(slot, now, zone);
  return (
    <div
      role="group"
      aria-label="Confirm: add focus time"
      className="flex flex-col gap-3 rounded-xl border border-accent/40 bg-accent-soft/60 px-4 py-3"
    >
      <p className="flex items-center gap-2 font-medium text-ink">
        <ShieldCheck aria-hidden className="size-4 text-accent-text" />
        Add this to your calendar?
      </p>
      <dl className="flex flex-col gap-1.5">
        <Row label="Title">{title}</Row>
        <Row label="When">
          <span className="tabular-nums">{words.label}</span>{" "}
          <span className="text-muted">({words.relative})</span>
        </Row>
        <Row label="Calendar">{email ? `${email} · main calendar` : "Your main calendar"}</Row>
        <Row label="Details">Private, no guests, a reminder 5 minutes before</Row>
      </dl>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="hidden text-sm text-muted sm:block">
          <Kbd>Enter</Kbd> to add · <Kbd>Esc</Kbd> to go back
        </p>
        <div className="flex gap-2">
          <Button size="sm" variant="ghost" onClick={onBack} disabled={busy}>
            Back
          </Button>
          <Button
            ref={confirmRef}
            size="sm"
            variant="primary"
            busy={busy}
            onKeyDown={(e) => {
              if (e.repeat) e.preventDefault();
            }}
            onClick={onConfirm}
            data-testid="make-time-confirm"
          >
            {busy ? "Adding…" : "Add to calendar"}
          </Button>
        </div>
      </div>
      <p className="text-xs text-muted">
        MeeMail checks your calendar again right before it adds this. You can undo it.
      </p>
    </div>
  );
}
